import React, { useEffect, useMemo, useState } from "react";
import { useHistory, Link } from "react-router-dom";

const API = "https://appapi.unpaidfinancial.com/api";

const Login = () => {
  const history = useHistory();

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const isMobile = windowWidth < 900;

  const [formData, setFormData] = useState({ email: "", pin: "" });
  const [showPin, setShowPin] = useState(false);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const styles = useMemo(() => {
    const hover = {
      linkIn: (e) => {
        e.currentTarget.style.color = "#3B82F6";
        e.currentTarget.style.transform = "translateX(2px)";
      },
      linkOut: (e, baseColor = "#2563EB") => {
        e.currentTarget.style.color = baseColor;
        e.currentTarget.style.transform = "translateX(0px)";
      },
      btnIn: (e) => {
        e.currentTarget.style.backgroundColor = "#1D4ED8";
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = "0 14px 26px -18px rgba(0,0,0,0.55)";
      },
      btnOut: (e) => {
        e.currentTarget.style.backgroundColor = "#2563EB";
        e.currentTarget.style.transform = "translateY(0px)";
        e.currentTarget.style.boxShadow = "none";
      },
      iconBtnIn: (e) => {
        e.currentTarget.style.backgroundColor = "rgba(15,23,42,0.08)";
        e.currentTarget.style.borderColor = "rgba(15,23,42,0.18)";
      },
      iconBtnOut: (e) => {
        e.currentTarget.style.backgroundColor = "rgba(15,23,42,0.04)";
        e.currentTarget.style.borderColor = "rgba(15,23,42,0.12)";
      },
    };

    return {
      hover,

      page: {
        minHeight: "100vh",
        padding: "2.25rem 1.25rem",
        background:
          "radial-gradient(1200px 600px at 10% -10%, #334155 0%, rgba(30,41,59,0) 60%), radial-gradient(1200px 600px at 110% 110%, #0ea5e9 0%, rgba(14,165,233,0) 55%), #0f172a",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },

      card: {
        width: "100%",
        maxWidth: 980,
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1.05fr 0.95fr",
        borderRadius: 16,
        overflow: "hidden",
        background: "#FFFFFF",
        boxShadow: "0 30px 60px -15px rgba(2,6,23,0.45)",
        border: "1px solid rgba(15,23,42,0.12)",
      },

      brandSide: {
        padding: isMobile ? "2rem 1.5rem" : "3rem",
        background: "linear-gradient(135deg, #0f172a, #111827)",
        color: "#E5E7EB",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "1rem",
      },

      badge: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".5rem",
        width: "fit-content",
        padding: ".5rem .75rem",
        borderRadius: 999,
        border: "1px solid rgba(255,255,255,0.16)",
        background: "rgba(255,255,255,0.06)",
        color: "#E5E7EB",
        fontWeight: 800,
        fontSize: ".85rem",
        letterSpacing: ".02em",
      },

      brandTitle: {
        margin: 0,
        fontSize: isMobile ? "1.6rem" : "2rem",
        lineHeight: 1.15,
        color: "#FFFFFF",
        fontWeight: 900,
      },

      brandText: {
        margin: 0,
        color: "rgba(229,231,235,0.78)",
        lineHeight: 1.6,
        fontSize: "0.98rem",
        maxWidth: 420,
      },

      brandPoints: {
        marginTop: "1rem",
        display: "grid",
        gap: ".75rem",
      },

      point: {
        display: "flex",
        gap: ".75rem",
        alignItems: "flex-start",
        color: "rgba(229,231,235,0.82)",
        fontSize: "0.95rem",
        lineHeight: 1.5,
      },

      pointIcon: {
        width: 34,
        height: 34,
        borderRadius: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(96,165,250,0.14)",
        border: "1px solid rgba(96,165,250,0.22)",
        color: "#93C5FD",
        flexShrink: 0,
      },

      formSide: {
        padding: isMobile ? "2rem 1.5rem" : "3rem",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "1rem",
      },

      formHeader: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "1rem",
        marginBottom: ".25rem",
      },

      h2: {
        margin: 0,
        fontSize: "1.35rem",
        fontWeight: 900,
        color: "#0F172A",
      },

      sub: {
        margin: ".35rem 0 0",
        color: "#64748B",
        fontSize: ".95rem",
        lineHeight: 1.5,
      },

      iconBtn: {
        width: 42,
        height: 42,
        borderRadius: 12,
        border: "1px solid rgba(15,23,42,0.12)",
        background: "rgba(15,23,42,0.04)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      },

      notif: (type) => ({
        padding: "0.85rem 0.9rem",
        borderRadius: 12,
        border: `1px solid ${
          type === "success" ? "rgba(16,185,129,0.35)" : "rgba(244,63,94,0.35)"
        }`,
        background:
          type === "success" ? "rgba(16,185,129,0.10)" : "rgba(244,63,94,0.10)",
        color: type === "success" ? "#065F46" : "#9F1239",
        fontWeight: 700,
        fontSize: ".95rem",
        lineHeight: 1.4,
      }),

      form: {
        display: "grid",
        gap: "0.9rem",
        marginTop: ".5rem",
      },

      field: {
        display: "grid",
        gap: ".45rem",
      },

      label: {
        fontSize: ".9rem",
        fontWeight: 700,
        color: "#0F172A",
      },

      inputWrap: {
        position: "relative",
      },

      input: {
        width: "100%",
        padding: "0.9rem 1rem",
        borderRadius: 12,
        border: "1px solid #E5E7EB",
        background: "#F8FAFC",
        fontSize: "1rem",
        outline: "none",
        color: "#0F172A",
        transition: "box-shadow .15s ease, border-color .15s ease, transform .15s ease",
      },

      inputFocus: (e) => {
        e.currentTarget.style.borderColor = "rgba(37,99,235,0.55)";
        e.currentTarget.style.boxShadow = "0 0 0 4px rgba(37,99,235,0.12)";
        e.currentTarget.style.transform = "translateY(-1px)";
      },
      inputBlur: (e) => {
        e.currentTarget.style.borderColor = "#E5E7EB";
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0px)";
      },

      togglePin: {
        position: "absolute",
        right: 8,
        top: "50%",
        transform: "translateY(-50%)",
        borderRadius: 10,
        border: "1px solid rgba(15,23,42,0.12)",
        background: "rgba(15,23,42,0.04)",
        padding: "0.55rem 0.7rem",
        fontWeight: 800,
        cursor: "pointer",
        color: "#0F172A",
      },

      row: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
        marginTop: ".25rem",
      },

      helper: {
        color: "#64748B",
        fontSize: ".92rem",
      },

      link: {
        color: "#2563EB",
        fontWeight: 800,
        textDecoration: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: ".35rem",
        transition: "all .2s ease",
      },

      btn: {
        width: "100%",
        padding: "0.95rem",
        backgroundColor: "#2563EB",
        color: "#FFFFFF",
        borderRadius: 12,
        fontWeight: 900,
        border: "none",
        marginTop: ".2rem",
        cursor: loading ? "not-allowed" : "pointer",
        transition: "all .2s ease",
      },

      bottomText: {
        textAlign: "center",
        marginTop: "1rem",
        color: "#64748B",
        fontSize: ".95rem",
      },
    };
  }, [isMobile, loading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "pin") {
      const onlyDigits = value.replace(/\D/g, "").slice(0, 4);
      setFormData((prev) => ({ ...prev, pin: onlyDigits }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotification(null);

    if (!formData.email || !formData.pin) {
      setNotification({ type: "error", message: "Tous les champs sont obligatoires." });
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API}/bank/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email.trim(),
          pin: formData.pin.trim(),
        }),
      });

      const payload = await res.json().catch(() => null);

      if (!res.ok) {
        const errMsg =
          (payload && (payload.error || payload.message)) ||
          `Échec de connexion (HTTP ${res.status}).`;
        setNotification({ type: "error", message: errMsg });
        return;
      }

      localStorage.setItem("bank.token", payload?.token || "");
      localStorage.setItem("bank.user", JSON.stringify(payload?.user || {}));
      localStorage.setItem("bank.account", JSON.stringify(payload?.account || {}));
      localStorage.setItem("bank.card", JSON.stringify(payload?.card || null));
      localStorage.setItem("bank.role", payload?.user?.role || "client");

      if (payload?.account?.iban) localStorage.setItem("bank.iban", payload.account.iban);
      if (payload?.account?.iban_formatted)
        localStorage.setItem("bank.iban_formatted", payload.account.iban_formatted);
      if (payload?.account?.iban_masked)
        localStorage.setItem("bank.iban_masked", payload.account.iban_masked);

      localStorage.setItem(
        "bank.session",
        JSON.stringify({
          token: payload?.token || null,
          user: payload?.user || {},
          account: payload?.account || {},
          card: payload?.card || null,
          role: payload?.user?.role || "client",
          logged_at: new Date().toISOString(),
        })
      );

      setNotification({ type: "success", message: "Connexion réussie. Redirection…" });

      setTimeout(() => {
        history.replace("/banque/portefeuille");
      }, 600);
    } catch (err) {
      setNotification({
        type: "error",
        message:
          err?.name === "TypeError"
            ? "Impossible d'atteindre le serveur."
            : err?.message || "Erreur réseau.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Brand */}
        <aside style={styles.brandSide}>
          <div style={styles.badge}>
            <span aria-hidden="true">🏦</span> AidFoundation Banque
          </div>

          <h1 style={styles.brandTitle}>Connexion sécurisée</h1>
          <p style={styles.brandText}>
            Accédez à votre portefeuille en toute sécurité avec votre e-mail et votre code PIN.
          </p>

          <div style={styles.brandPoints}>
            <div style={styles.point}>
              <div style={styles.pointIcon} aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2l8 4v6c0 5-3.4 9.4-8 10-4.6-.6-8-5-8-10V6l8-4z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M9 12l2 2 4-4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>Protection des accès et session sécurisée.</div>
            </div>

            <div style={styles.point}>
              <div style={styles.pointIcon} aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 8v4l3 3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <div>Connexion rapide, expérience fluide.</div>
            </div>
          </div>
        </aside>

        {/* Form */}
        <section style={styles.formSide}>
          <div style={styles.formHeader}>
            <div>
              <h2 style={styles.h2}>Se connecter</h2>
              <p style={styles.sub}>Entrez vos informations pour accéder à votre espace.</p>
            </div>

            <button
              type="button"
              style={{ ...styles.iconBtn, transition: "all .2s ease" }}
              onMouseEnter={styles.hover.iconBtnIn}
              onMouseLeave={styles.hover.iconBtnOut}
              onClick={() => history.push("/")}
              title="Retour"
              aria-label="Retour"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {notification && <div style={styles.notif(notification.type)}>{notification.message}</div>}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="email">
                Adresse e-mail
              </label>
              <input
                id="email"
                type="email"
                name="email"
                placeholder="ex: nom@domaine.com"
                value={formData.email}
                onChange={handleChange}
                style={styles.input}
                onFocus={styles.inputFocus}
                onBlur={styles.inputBlur}
                required
                autoComplete="email"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label} htmlFor="pin">
                Code PIN (4 chiffres)
              </label>
              <div style={styles.inputWrap}>
                <input
                  id="pin"
                  type={showPin ? "text" : "password"}
                  name="pin"
                  placeholder="••••"
                  value={formData.pin}
                  onChange={handleChange}
                  style={{ ...styles.input, paddingRight: "6.25rem" }}
                  onFocus={styles.inputFocus}
                  onBlur={styles.inputBlur}
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
                <button
                  type="button"
                  style={styles.togglePin}
                  onClick={() => setShowPin((v) => !v)}
                  aria-label={showPin ? "Masquer le PIN" : "Afficher le PIN"}
                >
                  {showPin ? "Masquer" : "Afficher"}
                </button>
              </div>
              <div style={{ ...styles.helper, marginTop: ".2rem" }}>
                Le PIN doit contenir exactement 4 chiffres.
              </div>
            </div>

            <button
              type="submit"
              style={styles.btn}
              disabled={loading}
              onMouseEnter={styles.hover.btnIn}
              onMouseLeave={styles.hover.btnOut}
            >
              {loading ? "Connexion…" : "Se connecter"}
            </button>

            <div style={styles.row}>
              <span style={styles.helper}>Pas encore de compte ?</span>
              <Link
                to="/register"
                style={styles.link}
                onMouseEnter={styles.hover.linkIn}
                onMouseLeave={(e) => styles.hover.linkOut(e, "#2563EB")}
              >
                Créer un compte →
              </Link>
            </div>
          </form>

          <div style={styles.bottomText}>
            Astuce : vérifiez votre e-mail et votre PIN avant de valider.
          </div>
        </section>
      </div>
    </div>
  );
};

export default Login;